/**
 * Icon Theme System for Network Graph Visualization
 *
 * Provides a pluggable architecture for device icons in network diagrams.
 * Each theme implements a function that receives device data and returns
 * the appropriate icon URL.
 */

/**
 * Device data available for icon selection
 * This mirrors what's available on Cytoscape node data
 */
export interface DeviceNodeData {
  id: string;
  label: string;
  deviceType: string;
  ipAddress: string;
  status: string;
  isMonitored: boolean;
  // Future: sysObjectId, sysDescr, vendor, model, etc.
}

/**
 * Icon theme interface
 *
 * Each theme implements this interface to provide device-to-icon mapping.
 * The getIconUrl function receives full device data and can use any
 * combination of fields to determine the appropriate icon.
 */
export interface IconTheme {
  /** Unique identifier for the theme */
  id: string;
  /** Display name for UI */
  name: string;
  /** Optional description */
  description?: string;
  /**
   * Get the icon URL for a device
   *
   * @param device - Device data from the topology
   * @returns URL path to the icon (e.g., '/icons/affinity/gray/c_switch.svg')
   */
  getIconUrl: (device: DeviceNodeData) => string;
}

// ============================================================================
// Affinity Theme (Public Domain icons from github.com/ecceman/affinity)
// ============================================================================

const AFFINITY_BASE = '/icons/affinity/gray';

/**
 * Map device types to Affinity icon filenames
 */
const AFFINITY_TYPE_MAP: Record<string, string> = {
  'switch': 'c_switch.svg',
  'router': 'c_router.svg',
  'firewall': 'c_firewall.svg',
  'server': 'c_server.svg',
  'workstation': 'c_client.svg',
  'pc': 'c_client.svg',
  'laptop': 'c_laptop.svg',
  'printer': 'c_printer.svg',
  'phone': 'c_ip_phone.svg',
  'ip-phone': 'c_ip_phone.svg',
  'camera': 'c_camera.svg',
  'wireless': 'c_wireless.svg',
  'access-point': 'c_wireless.svg',
  'ap': 'c_wireless.svg',
  'wap': 'c_wireless.svg',
  'wlc': 'c_wlc.svg',
  'storage': 'c_storage.svg',
  'nas': 'c_nas.svg',
  'load-balancer': 'c_loadbalancer.svg',
  'cloud': 'c_cloud.svg',
  'internet': 'c_globe.svg',
  'hub': 'c_hub.svg',
  'vm': 'c_vm.svg',
  'virtual-machine': 'c_vm.svg',
  'user': 'c_user.svg',
  'unknown': 'c_cog.svg',
  // SNMP-classified device types
  'host': 'c_client.svg',
  'layer3_switch': 'c_switch_multilayer.svg',
  'network_device': 'c_cog.svg',
};

/**
 * Affinity icon theme
 *
 * Uses clean, modern icons from the Affinity symbol set.
 * Gray circle style provides a consistent look.
 */
export const affinityTheme: IconTheme = {
  id: 'affinity',
  name: 'Affinity',
  description: 'Clean, modern network icons',
  getIconUrl: (device: DeviceNodeData): string => {
    // Normalize device type to lowercase for matching
    const deviceType = device.deviceType.toLowerCase();

    // Direct type match
    if (AFFINITY_TYPE_MAP[deviceType]) {
      return `${AFFINITY_BASE}/${AFFINITY_TYPE_MAP[deviceType]}`;
    }

    // Pattern matching for common variations
    if (deviceType.includes('switch')) {
      return `${AFFINITY_BASE}/c_switch.svg`;
    }
    if (deviceType.includes('router')) {
      return `${AFFINITY_BASE}/c_router.svg`;
    }
    if (deviceType.includes('firewall')) {
      return `${AFFINITY_BASE}/c_firewall.svg`;
    }
    if (deviceType.includes('server')) {
      return `${AFFINITY_BASE}/c_server.svg`;
    }
    if (deviceType.includes('wireless') || deviceType.includes('access')) {
      return `${AFFINITY_BASE}/c_wireless.svg`;
    }

    // Fallback to generic icon
    return `${AFFINITY_BASE}/c_cog.svg`;
  },
};

// ============================================================================
// Simple/No Icons Theme (for minimal view or debugging)
// ============================================================================

/**
 * No-icon theme - returns empty string, nodes render as plain circles
 */
export const noIconTheme: IconTheme = {
  id: 'none',
  name: 'No Icons',
  description: 'Simple circles without icons',
  getIconUrl: (): string => '',
};

// ============================================================================
// Theme Registry
// ============================================================================

/**
 * All available icon themes
 */
export const ICON_THEMES: Record<string, IconTheme> = {
  'affinity': affinityTheme,
  'none': noIconTheme,
};

/**
 * Default theme ID
 */
export const DEFAULT_THEME_ID = 'affinity';

/**
 * Get a theme by ID, with fallback to default
 */
export function getIconTheme(themeId: string): IconTheme {
  if (themeId in ICON_THEMES) {
    return ICON_THEMES[themeId];
  }
  return ICON_THEMES[DEFAULT_THEME_ID];
}

/**
 * Get list of available themes for UI selection
 */
export function getAvailableThemes(): Array<{ id: string; name: string; description?: string }> {
  return Object.values(ICON_THEMES).map((theme) => ({
    id: theme.id,
    name: theme.name,
    description: theme.description,
  }));
}

// ============================================================================
// localStorage persistence
// ============================================================================

const THEME_STORAGE_KEY = 'netman-icon-theme';

/**
 * Get the saved theme ID from localStorage
 */
export function getSavedThemeId(): string {
  if (typeof window === 'undefined') return DEFAULT_THEME_ID;
  return localStorage.getItem(THEME_STORAGE_KEY) || DEFAULT_THEME_ID;
}

/**
 * Save theme ID to localStorage
 */
export function saveThemeId(themeId: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(THEME_STORAGE_KEY, themeId);
}
